import React, { useState, useRef, useEffect } from 'react';

const EditableTextCell = ({ value, onSave, placeholder = '' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [localValue, setLocalValue] = useState(value ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const inputRef = useRef(null);
  const divRef = useRef(null);

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value ?? '');
    }
  }, [value, isEditing]);

  useEffect(() => {
    const el = divRef.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const startEdit = () => {
    setLocalValue(value ?? '');
    setIsEditing(true);
  };

  const save = async () => {
    const trimmed = localValue.trim();
    const prevValue = value ?? '';
    if (trimmed === prevValue || !onSave) { setIsEditing(false); return; }
    setIsSaving(true);
    try {
      await onSave(trimmed);
      setIsEditing(false);
    } catch {
      // onSave errors are handled by the caller; stay in edit mode
    } finally {
      setIsSaving(false);
    }
  };

  const cancel = () => {
    setLocalValue(value ?? '');
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    }
    if (e.key === 'Escape') {
      cancel();
    }
  };

  if (isEditing) {
    return (
      <div className='d-flex align-items-center w-100'>
        <input
          ref={inputRef}
          type='text'
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={save}
          onKeyDown={handleKeyDown}
          className='crm-table-editable-text-input specific-top-padding-2'
        />
        {isSaving ? (
          <span className='ml-1 bg-light-blue rounded-circle d-inline-flex align-items-center justify-content-center circle-button-24 flex-shrink-0'>
            <i className='fas fa-spinner fa-spin text-white paragraph-p3' />
          </span>
        ) : (
          <span
            className='icon-circle-check ml-1 text-light-blue title-h2 pointer hover-opacity-85'
            onMouseDown={(e) => e.preventDefault()}
            onClick={save}
            data-tooltip-content='Save'
            data-tooltip-id='general-tooltip'
            data-event-off='click'
          />
        )}
      </div>
    );
  }

  return (
    <div
      className='d-flex align-items-center w-100'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        ref={divRef}
        className='crm-table-editable-text flex-grow-1'
        {...(isTruncated &&
          value && {
            'data-tooltip-content': value,
            'data-tooltip-id': 'tooltip-mobile',
            'data-tooltip-class-name': 'header-tooltip pricing-tooltip-description pricing-tooltip-description',
          })}
      >
        {value ? <span>{value}</span> : <span className='crm-table-editable-text-placeholder'>{placeholder}</span>}
      </div>
      {isHovered && (
        <span
          className='ml-1 bg-light-blue rounded-circle d-inline-flex align-items-center justify-content-center circle-button-24 flex-shrink-0 pointer hover-opacity-85'
          onClick={startEdit}
          data-tooltip-content='Edit for your team'
          data-tooltip-id='general-tooltip'
          data-event-off='click'
        >
          <span className='icon-edit-pencil text-white paragraph-p5' />
        </span>
      )}
    </div>
  );
};

export default EditableTextCell;
